import { useCallback, useRef } from 'preact/hooks';

export interface LongPressHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerLeave: (e: PointerEvent) => void;
}

/**
 * Returns pointer event handlers that detect a long-press (hold ≥ threshold ms).
 * Calls onLongPress when the threshold is reached; suppresses the regular click.
 * Attaches to non-active dots only — active dot gets no handlers.
 */
export function useLongPress(
  onLongPress: () => void,
  threshold = 500,
): LongPressHandlers {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firedRef = useRef(false);

  const cancel = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onPointerDown = useCallback(
    (e: PointerEvent) => {
      if (e.button !== 0 && e.button !== -1) return; // left/touch only
      firedRef.current = false;
      cancel();
      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        timerRef.current = null;
        onLongPress();
      }, threshold);
    },
    [onLongPress, threshold, cancel],
  );

  const onPointerUp = useCallback(
    (e: PointerEvent) => {
      cancel();
      // If long-press fired, prevent the subsequent click event from also triggering.
      if (firedRef.current) {
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [cancel],
  );

  const onPointerLeave = useCallback(() => {
    cancel();
  }, [cancel]);

  return { onPointerDown, onPointerUp, onPointerLeave };
}
